// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI (owner, 2026-11-05: "now the AI needs to know about the Solar System
 * mode and how to operate it") — the chatbot's `solar.*` commands (AYNI 3D's
 * shared protocol, off there, on here) over Solar System mode
 * (`ui/solarSystemMode.ts`), in the browser and in a headset alike:
 *   - `solar.open`: the orrery (from a body, back to it);
 *   - `solar.visit`: a body's globe and datasets. From another body it flies
 *     when the visitor chose flights, else it fades: the chatbot never
 *     leaves a turn waiting on the comfort question;
 *   - `solar.view`: the layout and the clock (play, speed, today);
 *   - `solar.close`: back to the globe and the dataset shown before.
 * Each answers an AgentToolOutcome with what is on screen afterwards, and
 * `solarSituation` tells the model each turn while the mode is on.
 */

import type { AgentCommand, AgentSolarBody, AgentSolarSpeed, AgentToolOutcome } from '../ayni3d/shared/protocol'
import { ok, unavailable } from './xrChatOutcomes'

export type XrSolarState = 'off' | 'overview' | 'body'

/** What the executor needs of Solar System mode (main.ts lends its handle). */
export interface XrChatSolar {
  readonly state: XrSolarState
  readonly body: AgentSolarBody | null
  readonly layout: 'distances' | 'sizes'
  readonly timeMs: number
  readonly playing: boolean
  readonly speed: AgentSolarSpeed
  /** How the visitor chose to travel between bodies, null before the first flight. */
  readonly travel: 'fly' | 'fade' | null
  /** A body's name in the visitor's language. */
  name(body: AgentSolarBody): string
  enter(): Promise<void>
  leave(): Promise<void>
  visit(body: AgentSolarBody): Promise<void>
  travelTo(body: AgentSolarBody): Promise<void>
  setLayout(layout: 'distances' | 'sizes'): void
  togglePlay(): void
  cycleRate(): void
  today(): void
  /** False in a meeting's audience: the presenter's globe is not theirs to change. */
  mayChange(): boolean
}

const SPEED_WORDS: Record<AgentSolarSpeed, string> = { day: 'a day a second', week: 'a week a second', month: 'a month a second', year: 'a year a second' }
const LAYOUT_WORDS = { distances: 'true distances, bodies as dots', sizes: 'true sizes, distances squeezed' } as const

const isoDay = (ms: number): string => new Date(ms).toISOString().slice(0, 10)

/** How long a turn waits for a fade, a flight or a visit to arrive (a flight is 5 s at most, then the dataset loads). */
export const SOLAR_WAIT_MS = 15_000

/** Waits for the view to arrive, but never holds the turn past SOLAR_WAIT_MS (a fade whose frames stop must not freeze it). */
async function arrive(work: Promise<void>): Promise<void> {
  let timer: ReturnType<typeof setTimeout> | undefined
  await Promise.race([work.catch(() => {}), new Promise<void>((resolve) => { timer = setTimeout(resolve, SOLAR_WAIT_MS) })])
  clearTimeout(timer)
}

/** What is on screen now, as the outcome's values. */
function onScreen(solar: XrChatSolar): Record<string, unknown> {
  if (solar.state === 'off') return { solarSystem: 'off' }
  if (solar.state === 'body' && solar.body) return { solarSystem: 'visiting', body: solar.name(solar.body) }
  return {
    solarSystem: 'the whole Solar System',
    layout: LAYOUT_WORDS[solar.layout],
    date: isoDay(solar.timeMs),
    clock: solar.playing ? `running, ${SPEED_WORDS[solar.speed]}` : 'stopped',
  }
}

const METHOD = 'Solar System mode: the Sun, the planets, the Moon, Ceres and Pluto where they are on its clock\'s date (orbital elements, JPL).'

/** Carry out one `solar.*` command; resolves once the view has arrived. */
export async function solarCommand(solar: XrChatSolar | null, command: Extract<AgentCommand, { type: `solar.${string}` }>): Promise<AgentToolOutcome> {
  if (!solar) return unavailable('no-solar', 'This page has no Solar System mode.')
  if (!solar.mayChange()) return unavailable('meeting', 'In this meeting the presenter decides what the globe shows.')
  switch (command.type) {
    case 'solar.open':
      await arrive(solar.enter())
      break
    case 'solar.close':
      if (solar.state !== 'off') await arrive(solar.leave())
      break
    case 'solar.visit': {
      if (solar.state === 'body' && solar.body === command.body) break
      if (solar.state === 'off') await arrive(solar.enter())
      // A flight only when the visitor chose flights; else a fade, never the question in a chatbot's turn.
      if (solar.state === 'body' && solar.travel === 'fly') await arrive(solar.travelTo(command.body))
      else await arrive(solar.visit(command.body))
      break
    }
    case 'solar.view': {
      if (solar.state === 'off' || solar.state === 'body') await arrive(solar.enter())
      if (command.layout && command.layout !== solar.layout) solar.setLayout(command.layout)
      if (command.today) solar.today()
      if (command.speed) {
        for (let i = 0; i < 4 && solar.speed !== command.speed; i++) solar.cycleRate()
      }
      if (command.play !== undefined && command.play !== solar.playing) solar.togglePlay()
      break
    }
  }
  return ok(onScreen(solar), METHOD)
}

/** The turn's context while the mode is on (null when it is off, so nothing is added). */
export function solarSituation(solar: XrChatSolar | null): string | null {
  if (!solar || solar.state === 'off') return null
  if (solar.state === 'body' && solar.body) {
    return `Solar System mode is visiting ${solar.name(solar.body)}: its globe and datasets are on screen, and Browse keeps to them; solar_open goes back to the whole system, solar_close back to the Earth's globe.`
  }
  return `Solar System mode shows the whole Solar System (${LAYOUT_WORDS[solar.layout]}), its clock at ${isoDay(solar.timeMs)}, ${solar.playing ? `running ${SPEED_WORDS[solar.speed]}` : 'stopped'}.`
}
